<template>
  <div
    class="message-bell"
    @mouseenter="showPanel"
    @mouseleave="hidePanel"
    @click.stop
  >
    <button
      class="message-bell__button"
      type="button"
      aria-label="消息中心"
      @click="showPanel"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
        />
      </svg>
      <span v-if="unreadTotal" class="message-bell__badge">{{
        unreadTotal
      }}</span>
    </button>

    <div
      v-if="panelVisible"
      class="message-bell__panel"
      @mouseenter="showPanel"
    >
      <div class="message-bell__panel-head">
        <span>消息中心</span>
        <button type="button" @click="markAllAsRead">全部已读</button>
      </div>
      <div class="message-bell__list">
        <button
          v-for="item in previewMessages"
          :key="item.id"
          type="button"
          class="message-bell__item"
          @click="openMessage(item, true)"
        >
          <span class="message-bell__item-icon" :class="item.iconType">
            <svg
              v-if="item.iconType === 'tg'"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
              <path d="M22 12A10 10 0 0 0 12 2v10z" />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M15 11.25l-3-3m0 0l-3 3m3-3v7.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </span>
          <span class="message-bell__item-body">
            <span class="message-bell__item-title">
              <span class="message-bell__item-title-text">
                <template v-if="item.titleAddressParts">
                  {{ item.titleAddressParts.prefix }}
                  <el-tooltip
                    :content="item.titleAddressParts.fullAddress"
                    placement="top"
                    popper-class="Alltooltip-popper-style"
                  >
                    <span class="message-bell__address-short">{{
                      item.titleAddressParts.address
                    }}</span>
                  </el-tooltip>
                  {{ item.titleAddressParts.suffix }}
                </template>
                <template v-else>{{ item.title }}</template>
              </span>
              <i v-if="!item.isRead" class="message-bell__item-dot"></i>
            </span>
            <span class="message-bell__item-content">
              <template v-if="item.contentAddressParts">
                {{ item.contentAddressParts.prefix }}
                <el-tooltip
                  :content="item.contentAddressParts.fullAddress"
                  placement="top"
                  popper-class="Alltooltip-popper-style"
                >
                  <span class="message-bell__address-short">{{
                    item.contentAddressParts.address
                  }}</span>
                </el-tooltip>
                {{ item.contentAddressParts.suffix }}
              </template>
              <template v-else>{{ item.content }}</template>
            </span>
            <span class="message-bell__item-footer">
              <span class="message-bell__item-time">{{ item.time }}</span>
              <span
                v-if="item.actionText"
                class="message-bell__item-action"
                @click.stop="openMessage(item, true)"
              >
                {{ item.actionText }}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="m9 5 7 7-7 7" />
                </svg>
              </span>
            </span>
          </span>
        </button>
      </div>
      <button
        type="button"
        class="message-bell__all"
        @click="goToMessageCenter"
      >
        查看全部消息
      </button>
    </div>
  </div>
</template>

<script>
import {
  getNotificationList,
  getNotificationUnreadCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "@/aips/notification";
import {
  dispatchNotificationRefresh,
  dispatchTgAnalysisRefresh,
  getNotificationPageData,
  isSameAddress,
  isTaskRewardNotification,
  NOTIFICATION_REFRESH_EVENT,
  resolveNotificationRoute,
} from "@/utils/messageCenter";

let notificationOwnerToken = "";
let notificationSnapshotReady = false;
const knownNotificationIds = new Set();

function getNotificationIdentity(notification) {
  if (notification.id !== null && notification.id !== undefined) {
    return String(notification.id);
  }
  return [
    notification.eventType,
    notification.createdAt,
    notification.route,
  ].join(":");
}

export default {
  name: "MessageBell",
  data() {
    return {
      messages: [],
      unreadTotal: 0,
      panelVisible: false,
      panelHideTimer: null,
      refreshPromise: null,
    };
  },
  computed: {
    previewMessages() {
      return this.messages
        .slice()
        .sort((left, right) => Number(left.isRead) - Number(right.isRead))
        .slice(0, 2);
    },
  },
  methods: {
    showPanel() {
      if (this.panelHideTimer) {
        window.clearTimeout(this.panelHideTimer);
        this.panelHideTimer = null;
      }
      if (this.panelVisible) return;
      this.panelVisible = true;
      this.refreshMessages();
    },
    hidePanel() {
      if (this.panelHideTimer) window.clearTimeout(this.panelHideTimer);
      // Allow the pointer to travel from the bell to the absolutely positioned panel.
      this.panelHideTimer = window.setTimeout(() => {
        this.panelVisible = false;
        this.panelHideTimer = null;
      }, 500);
    },
    async refreshMessages(event) {
      const token = window.localStorage.getItem("t_k") || "";
      if (!token) {
        this.messages = [];
        this.unreadTotal = 0;
        return;
      }
      if (notificationOwnerToken !== token) {
        notificationOwnerToken = token;
        notificationSnapshotReady = false;
        knownNotificationIds.clear();
      }
      if (this.refreshPromise) {
        if (event && event.detail && event.detail.reason === "new-message") {
          return this.refreshPromise.then(() => this.refreshMessages(event));
        }
        return this.refreshPromise;
      }
      this.refreshPromise = (async () => {
        const listRequest = getNotificationList({ page: 1, size: 100 }).catch(
          () => null,
        );
        const countRequest = getNotificationUnreadCount().catch(() => null);
        const [listResponse, countResponse] = await Promise.all([
          listRequest,
          countRequest,
        ]);

        // A logout or token renewal may happen while the requests are in
        // flight. Do not let the old response repopulate the current session.
        if ((window.localStorage.getItem("t_k") || "") !== token) return;

        let hiddenUnreadCount = 0;
        if (listResponse) {
          const pageData = getNotificationPageData(listResponse, {
            compact: true,
            includeTaskRewards: true,
          });
          hiddenUnreadCount = pageData.hiddenUnreadCount;
          const nextMessages = pageData.messages;
          const taskRewardMessages = nextMessages.filter(
            isTaskRewardNotification,
          );
          const visibleMessages = nextMessages.filter(
            (item) => !isTaskRewardNotification(item),
          );
          const refreshReason = event && event.detail && event.detail.reason;
          if (notificationSnapshotReady || refreshReason === "new-message") {
            nextMessages
              .filter((item) => {
                const identity = getNotificationIdentity(item);
                return (
                  !item.isRead &&
                  !knownNotificationIds.has(identity)
                );
              })
              .forEach((item) => {
                if (isTaskRewardNotification(item)) {
                  this.showTaskRewardNotification(item);
                } else if (item.iconType === "tg") {
                  this.showAnalysisCompletionNotification(item);
                }
              });
          }
          nextMessages.forEach((item) => {
            knownNotificationIds.add(getNotificationIdentity(item));
          });
          notificationSnapshotReady = true;
          this.messages = visibleMessages;
          this.markTaskRewardsAsRead(
            taskRewardMessages.filter((item) => !item.isRead),
          );
        }
        if (countResponse && countResponse.code === 200) {
          this.unreadTotal = Math.max(
            0,
            Number(countResponse.data || 0) - hiddenUnreadCount,
          );
        }
      })().finally(() => {
        this.refreshPromise = null;
      });

      return this.refreshPromise;
    },
    showTaskRewardNotification(item) {
      this.$notify({
        title: item.title || "任务奖励到账",
        message: item.content || "任务奖励积分已到账",
        type: "success",
        customClass: "tb-custom-notification",
        duration: 5000,
        position: "top-right",
      });
    },
    async markTaskRewardsAsRead(items) {
      if (!items.length) return;
      const results = await Promise.all(
        items.map((item) => this.markTaskRewardAsRead(item)),
      );
      if (results.some(Boolean)) {
        dispatchNotificationRefresh("task-reward-read");
      }
    },
    async markTaskRewardAsRead(item) {
      try {
        const response = await markNotificationAsRead(item.id);
        if (response && response.code === 200) {
          item.isRead = true;
          item.status = 1;
          return true;
        }
      } catch (error) {
        // 奖励消息仅用于即时提醒，已读失败等待后续刷新重试。
      }
      return false;
    },
    showAnalysisCompletionNotification(item) {
      this.$notify({
        title: "分析任务完成",
        message:
          item.content || "TG公群舆情数据分析已完成， 请前往详情查看",
        type: "success",
        customClass: "tb-custom-notification",
        duration: 5000,
        position: "top-right",
      });

      const target = resolveNotificationRoute(item);
      const currentAddress =
        this.$route && this.$route.query && this.$route.query.address;
      const targetAddress = target && target.query && target.query.address;
      if (
        target &&
        target.path === (this.$route && this.$route.path) &&
        // EVM 地址大小写（EIP-55 校验和）不同也算同一地址，否则刷新会被静默丢弃
        isSameAddress(currentAddress, targetAddress)
      ) {
        dispatchTgAnalysisRefresh(targetAddress);
      }
    },
    async markAllAsRead() {
      if (!this.unreadTotal) return;
      try {
        const response = await markAllNotificationsAsRead();
        if (!response || response.code !== 200) return;
        this.messages = this.messages.map((item) => ({
          ...item,
          isRead: true,
          status: 1,
        }));
        this.unreadTotal = 0;
        dispatchNotificationRefresh("read-all");
        this.$tbMessage({
          type: "success",
          text: "全部消息已标记为已读",
        });
      } catch (error) {
        // 请求层统一提示错误
      }
    },
    async openMessage(item, markAsRead = false) {
      if (markAsRead && !item.isRead) {
        try {
          const response = await markNotificationAsRead(item.id);
          if (response && response.code === 200) {
            item.isRead = true;
            item.status = 1;
            this.unreadTotal = Math.max(0, this.unreadTotal - 1);
            dispatchNotificationRefresh("read");
          }
        } catch (error) {
          // 已读失败不阻止用户打开消息目标
        }
      }
      this.panelVisible = false;
      const target = resolveNotificationRoute(item);
      if (target) {
        const currentAddress =
          (this.$route.query && this.$route.query.address) || "";
        if (
          target.path === this.$route.path &&
          target.query &&
          isSameAddress(target.query.address, currentAddress)
        ) {
          dispatchTgAnalysisRefresh(target.query.address);
          return;
        }
        this.$router.push(target).catch(() => {});
      } else {
        this.goToMessageCenter();
      }
    },
    goToMessageCenter() {
      this.panelVisible = false;
      this.$router.push("/message-center");
    },
    closeOnOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.panelVisible = false;
      }
    },
  },
  mounted() {
    window.addEventListener(NOTIFICATION_REFRESH_EVENT, this.refreshMessages);
    document.addEventListener("click", this.closeOnOutsideClick);
    this.refreshMessages();
  },
  beforeDestroy() {
    window.removeEventListener(
      NOTIFICATION_REFRESH_EVENT,
      this.refreshMessages,
    );
    document.removeEventListener("click", this.closeOnOutsideClick);
    if (this.panelHideTimer) window.clearTimeout(this.panelHideTimer);
  },
};
</script>

<style lang="less" scoped>
.message-bell {
  position: relative;
  display: flex;
  align-items: center;
  margin-right: 16px;
  height: 100%;

  &__button {
    position: relative;
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    color: #9ca3af;
    background: rgba(255, 255, 255, 0.08);
    cursor: pointer;
    transition: all 0.2s;

    &:hover,
    &:focus-visible {
      color: #3b82f6;
      background: rgba(59, 130, 246, 0.15);
      outline: none;
    }
  }

  &__badge {
    position: absolute;
    top: 2px;
    right: 1px;
    min-width: 16px;
    height: 16px;
    box-sizing: border-box;
    border: 2px solid #09081d;
    border-radius: 999px;
    color: #fff;
    background: #ef4444;
    font-size: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__panel {
    position: absolute;
    top: 52px;
    right: 0;
    width: 280px;
    overflow: hidden;
    border-radius: 0 0 8px 8px;
    background: rgba(15, 23, 42, 0.98);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6);
    z-index: 1001;
  }

  &__panel-head,
  &__item,
  &__all {
    width: 100%;
    box-sizing: border-box;
  }

  &__panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    color: #fff;
    font-size: 14px;
    font-weight: 600;

    button {
      padding: 0;
      border: 0;
      color: #3b82f6;
      background: transparent;
      font-size: 12px;
      cursor: pointer;
    }
  }

  &__list {
    overflow: hidden;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px 16px;
    border: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    color: #d1d5db;
    background: transparent;
    text-align: left;
    cursor: pointer;

    &:hover {
      background: rgba(255, 255, 255, 0.06);
    }
  }

  &__item-icon {
    display: inline-flex;
    flex: 0 0 18px;
    margin-top: 2px;

    svg {
      width: 18px;
      height: 18px;
    }

    &.info {
      color: #3b82f6;
    }

    &.system {
      color: #3b82f6;
    }

    &.tg {
      color: #10b981;
    }

    &.warn {
      color: #f59e0b;
    }

    &.error {
      color: #ef4444;
    }
  }

  &__item-body {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
    gap: 5px;
  }

  &__item-title {
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
    gap: 6px;
    color: #fff;
    font-size: 13px;
  }

  &__item-title-text {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__item-dot {
    flex: 0 0 auto;
    width: 6px;
    height: 6px;
    margin-left: auto;
    border-radius: 50%;
    background: #ef4444;
  }

  &__item-content {
    overflow: hidden;
    color: #9ca3af;
    font-size: 12px;
    line-height: 1.45;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__item-time {
    color: #4b5563;
    font-size: 11px;
  }

  &__item-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-width: 0;
    gap: 8px;
  }

  &__item-action {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 2px;
    padding: 4px 8px;
    border-radius: 4px;
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.15);
    font-size: 12px;
    line-height: 1;
    cursor: pointer;

    svg {
      width: 12px;
      height: 12px;
    }
  }

  &__all {
    display: block;
    padding: 13px 16px;
    border: 0;
    color: #3b82f6;
    background: transparent;
    font-size: 13px;
    text-align: center;
    cursor: pointer;

    &:hover {
      background: rgba(255, 255, 255, 0.06);
    }
  }
}
</style>
